import {castDraft, type Draft} from 'immer'

import type {Diagnostic} from '@codemirror/lint'
import type {PayloadAction} from '@reduxjs/toolkit'
import {isAnyOf, createSlice} from '@reduxjs/toolkit'
import deepEqual from 'fast-deep-equal'

import {assignIfDeepDifferent, mergeIfDifferentAndPrune} from '../../../../../reducers/utils'
import type {PipelineId} from '../../../../../types'
import {pipelinesApi} from '../../services/pipelinesApi'
import {ConfirmationDecision, type Pipeline} from '../../services/pipelinesApi.types'
import type {PipelineDependencyDetails, PipelineDependencyId, SuggestionType} from '../../types'
import {matchError} from '../../utils/validators'
import {hasNoStoredPipelineYaml} from '../EditPipelinePage.consts'
import {parsePipeline} from '../EditPipelinePage.utils'

import * as pipelineDraftAgent from './EditPipelinePage.agent.slice'
import * as pipelineDraftBranch from './EditPipelinePage.branch.slice'
import * as pipelineDraftDependencies from './EditPipelinePage.dependencies.slice'
import * as pipelineDraftFilesPublicationJob from './EditPipelinePage.filesPublicationJob.slice'
import * as pipelineDraftIntegration from './EditPipelinePage.integration.slice'
import * as pipelineDraftJob from './EditPipelinePage.job.slice'
import * as pipelineDraftJobParameters from './EditPipelinePage.jobParameters.slice'
import * as pipelineNotifications from './EditPipelinePage.notifications.slice'
import * as pipelineDraftOutputParameters from './EditPipelinePage.outputParameters.slice'
import * as pipelineDraftParameter from './EditPipelinePage.parameter.slice'
import * as pipelineDraftMain from './EditPipelinePage.pipeline.slice'
import * as pipelineDraftPipelineDependencies from './EditPipelinePage.pipelineDependencies.slice'
import * as pipelineDraftSecret from './EditPipelinePage.secret.slice'
import {
  collapsedBlocksInitialState,
  debugInitialState,
  initialState,
  pipelineDependencyDetailsInitialState,
  pipelineFormInitialState,
  pipelineYamlInitialState,
  pipelineYamlValidityState,
  pipelineKotlinDslInitialState,
  suggestionsInitialState,
} from './EditPipelinePage.slices.consts'
import type {
  FormType,
  JobDependencyGuideState,
  PipelineFormParams,
  PipelineYamlValidityState,
} from './EditPipelinePage.slices.types'
import {
  deleteIfEmpty,
  deleteNestedIfEmpty,
  ensureDebugEntry,
  ensureDependencyDetailsEntry,
  ensurePipelineDraftEntry,
  ensureYamlDraftEntry,
  getDebugEntry,
  getEffectivePipelineBranch,
  getPipelineDraftEntry,
  getPipelineFormKey,
  setYamlValidityEntry,
} from './EditPipelinePage.slices.utils'
import * as pipelineDraftSteps from './EditPipelinePage.steps.slice'
import * as pipelineDraftTrigger from './EditPipelinePage.trigger.slice'
import * as pipelineDraftVersionedSettings from './EditPipelinePage.versionedSettings.slice'

export const pipelineDraft = createSlice({
  name: 'pipelineDraft',
  initialState,
  reducers: {
    ...pipelineDraftMain,
    ...pipelineDraftDependencies,
    ...pipelineDraftBranch,
    ...pipelineDraftSteps,
    ...pipelineDraftJob,
    ...pipelineDraftParameter,
    ...pipelineDraftIntegration,
    ...pipelineDraftSecret,
    ...pipelineDraftFilesPublicationJob,
    ...pipelineDraftTrigger,
    ...pipelineDraftAgent,
    ...pipelineDraftVersionedSettings,
    ...pipelineNotifications,
    ...pipelineDraftJobParameters,
    ...pipelineDraftOutputParameters,
    ...pipelineDraftPipelineDependencies,
  },
  extraReducers: builder => {
    builder.addMatcher(pipelinesApi.endpoints.getPipelineById.matchFulfilled, (state, action) => {
      const {originalArgs, forceRefetch} = action.meta.arg
      const {id, branch} = originalArgs
      const data = action.payload
      const effectiveBranch = getEffectivePipelineBranch(data, branch)
      const apiPipelineVersion = data.pipelineVersion
      const pipeline = ensurePipelineDraftEntry(state, id, effectiveBranch)
      const originalPipelineVersion = pipeline.original?.pipelineVersion
      const isCollisionApi =
        originalPipelineVersion != null && apiPipelineVersion !== originalPipelineVersion

      if (!isCollisionApi) {
        const draft = pipeline.draft
        if (draft != null) {
          // TODO We need to change this logic to avoid modifying the draft
          if (data.vcsRoot) {
            draft.vcsRoot =
              draft.vcsRoot != null
                ? mergeIfDifferentAndPrune(draft.vcsRoot, data.vcsRoot)
                : data.vcsRoot
          } else if (deepEqual(draft.vcsRoot, pipeline.original?.vcsRoot)) {
            // Server reports no main root, and the draft root still equals the
            // last-saved root (no local edit) — it's a stale saved root removed
            // on the server, so drop it. An unsaved local root change (freshly
            // added or a selected existing root) differs from the saved state and
            // is preserved. null matches the server's `vcsRoot: null` so the
            // draft isn't seen as dirty.
            draft.vcsRoot = null
          }
        }

        assignIfDeepDifferent(pipeline, {
          original: data,
          draft: draft ?? data,
        })
      } else if (forceRefetch) {
        assignIfDeepDifferent(pipeline, {
          original: data,
          draft: data,
        })
      }

      pipeline.isCollisionApi = isCollisionApi && !forceRefetch
    })
    builder.addMatcher(pipelinesApi.endpoints.updatePipeline.matchFulfilled, (state, action) => {
      const {id, commitDetails} = action.meta.arg.originalArgs
      const pipelineState = getPipelineDraftEntry(state, id, commitDetails?.branch ?? undefined)
      if (pipelineState?.draft == null) {
        return
      }

      // After save, the server response no longer reflects transient client-side
      // markers. Clear the isNew flag so reopening the sidebar treats the saved
      // repository as existing (Edit Repository, locked identity for root).
      if (pipelineState.draft.vcsRoot?.isNew) {
        delete pipelineState.draft.vcsRoot.isNew
      }
      pipelineState.draft.additionalVcsRoots?.forEach(root => {
        if (root.isNew) {
          delete root.isNew
        }
      })

      pipelineState.original = castDraft(action.payload)
    })
  },
})

export const pipelineDraftForm = createSlice({
  name: 'pipelineDraftForm',
  initialState: {
    submitted: false,
  },
  reducers: {
    submit: state => {
      state.submitted = true
    },
  },
  extraReducers: builder => {
    builder.addMatcher(isAnyOf(pipelineDraft.actions.set, pipelineDraft.actions.reset), state => {
      state.submitted = false
    })
  },
})

export const hoveredJob = createSlice({
  name: 'hoveredJob',
  initialState: null as string | null,
  reducers: {
    set: (_, action: PayloadAction<string | null>) => action.payload,
  },
})

export const jobDependencyGuide = createSlice({
  name: 'jobDependencyGuide',
  initialState: null as JobDependencyGuideState,
  reducers: {
    show: (_, action: PayloadAction<NonNullable<JobDependencyGuideState>>) => action.payload,
    hide: () => null,
  },
  extraReducers: builder => {
    builder.addCase(pipelineDraft.actions.changeJobId, (state, action) => {
      const {pipelineId, branch, jobId, newId} = action.payload
      if (state?.pipelineId === pipelineId && state.branch === branch && state.jobId === jobId) {
        state.jobId = newId
      }
    })
  },
})

export const collapsedBlocks = createSlice({
  name: 'collapsedBlocks',
  initialState: collapsedBlocksInitialState,
  reducers: {
    toggle(state, action: PayloadAction<{id: string; collapsed: boolean}>) {
      const {id, collapsed} = action.payload
      state[id] = collapsed
    },
  },
})

export const pipelineForm = createSlice({
  name: 'pipelineForm',
  initialState: pipelineFormInitialState,
  reducers: {
    open(state, action: PayloadAction<PipelineFormParams>) {
      const {pipelineId, ...rest} = action.payload
      state[pipelineId] ??= {}
      state[pipelineId][getPipelineFormKey(rest)] = {open: true, isAdd: action.payload.isAdd}
    },
    close(state, action: PayloadAction<PipelineFormParams>) {
      const {pipelineId, ...rest} = action.payload
      if (state[pipelineId] != null) {
        state[pipelineId][getPipelineFormKey(rest)] = {open: false}
      }
    },
    closeAll(state, action: PayloadAction<string>) {
      delete state[action.payload]
    },
  },
})

export const pipelineYaml = createSlice({
  name: 'pipelineYaml',
  initialState: pipelineYamlInitialState,
  reducers: {
    setYaml: (state, action: PayloadAction<{id: PipelineId; branch: string; yaml: string}>) => {
      const {id, branch, yaml} = action.payload
      const yamlDraftEntry = ensureYamlDraftEntry(state, id, branch)
      yamlDraftEntry.yaml = yaml
    },
    setDiagnostics: (
      state,
      action: PayloadAction<{
        id: PipelineId
        branch: string
        diagnostics: Omit<Diagnostic, 'actions'>[]
      }>,
    ) => {
      const {id, branch, diagnostics} = action.payload
      const yamlDraftEntry = ensureYamlDraftEntry(state, id, branch)
      yamlDraftEntry.diagnostics = diagnostics
    },
    reset: (state, action: PayloadAction<{id: PipelineId; branch: string}>) => {
      const {id, branch} = action.payload
      const byBranch = state[id]
      if (byBranch != null) {
        delete byBranch[branch]
      }
    },
  },
})

const applyYamlValidity = (
  state: Draft<PipelineYamlValidityState>,
  data: Pipeline,
  requestedBranch?: string,
) => {
  const {id, yaml = '', versionedSettings} = data
  const branch = getEffectivePipelineBranch(data, requestedBranch)
  try {
    if (yaml !== '') {
      parsePipeline(yaml)
      setYamlValidityEntry(state, id, {isParsed: true}, branch)
    } else if (hasNoStoredPipelineYaml(yaml, versionedSettings)) {
      setYamlValidityEntry(state, id, {isParsed: true}, branch)
    } else {
      setYamlValidityEntry(state, id, {isParsed: false, yaml}, branch)
    }
  } catch (e) {
    setYamlValidityEntry(state, id, {isParsed: false, yaml}, branch)
  }
}

export const pipelineYamlValidity = createSlice({
  name: 'pipelineYamlValidity',
  initialState: pipelineYamlValidityState,
  reducers: {
    setParsed: (
      state,
      action: PayloadAction<{id: PipelineId; branch: string; value: boolean; yaml?: string}>,
    ) => {
      const {id, branch, value, yaml} = action.payload
      setYamlValidityEntry(state, id, {isParsed: value, yaml}, branch)
    },
  },
  extraReducers: builder => {
    builder.addMatcher(pipelinesApi.endpoints.getPipelineById.matchFulfilled, (state, action) => {
      const data = action.payload
      if (data) {
        applyYamlValidity(state, data, action.meta.arg.originalArgs.branch)
      }
    })

    builder.addMatcher(pipelinesApi.endpoints.updatePipeline.matchFulfilled, (state, action) => {
      const {pipeline, commitDetails} = action.meta.arg.originalArgs
      if (pipeline.versionedSettings?.confirmationDecision === ConfirmationDecision.IMPORT) {
        applyYamlValidity(state, action.payload, commitDetails?.branch ?? undefined)
      }
    })
  },
})

export const suggestions = createSlice({
  name: 'suggestions',
  initialState: suggestionsInitialState,
  reducers: {
    skip(state, action: PayloadAction<{id: string; values: string[]}>) {
      const {id, values} = action.payload
      state.skippedSuggestions[id] = values
    },
    showSuccessMessage(state, action: PayloadAction<{id: string; type: SuggestionType}>) {
      const {id, type} = action.payload
      state.successMessages[id] = type
    },
    hideSuccessMessage(state, action: PayloadAction<{id: string}>) {
      const {id} = action.payload
      state.successMessages[id] = null
    },
  },
})

export type PipelineError = {
  pipelineId: PipelineId
  branch: string
  jobId?: string | null
  formType?: FormType
  formId?: string | number
  fieldName?: string
  message?: string
}
const pipelineErrorsInitialState: PipelineError[] = []
export const pipelineErrors = createSlice({
  name: 'pipelineErrors',
  initialState: pipelineErrorsInitialState,
  reducers: {
    add(state, action: PayloadAction<PipelineError>) {
      state.push(action.payload)
    },
    clear(state, action: PayloadAction<PipelineError>) {
      const condition = (error: PipelineError) => !matchError(action.payload)(error)
      if (!state.every(condition)) {
        return state.filter(condition)
      }
      return undefined
    },
  },
})

type PipelineSecretsState = Record<string, Record<string, Record<string, string>>>
const pipelineSecretsInitialState: PipelineSecretsState = {}
export const pipelineSecrets = createSlice({
  name: 'pipelineSecrets',
  initialState: pipelineSecretsInitialState,
  reducers: {
    set: (
      state,
      action: PayloadAction<{
        pipelineId: PipelineId
        branch: string
        savedName: string
        value: string
      }>,
    ) => {
      const {pipelineId, branch, savedName, value} = action.payload
      const byBranch = (state[pipelineId] ??= {})
      const branchSecrets = (byBranch[branch] ??= {})
      branchSecrets[savedName] = value
    },
    unset: (
      state,
      action: PayloadAction<{pipelineId: PipelineId; branch: string; savedName: string}>,
    ) => {
      const {pipelineId, branch, savedName} = action.payload
      delete state[pipelineId]?.[branch]?.[savedName]
      deleteNestedIfEmpty(state, pipelineId, branch)
    },
    unsetAll: (state, action: PayloadAction<{pipelineId: PipelineId; branch: string}>) => {
      const {pipelineId, branch} = action.payload
      delete state[pipelineId]?.[branch]
      deleteIfEmpty(state, pipelineId)
    },
  },
})

type StepSecretEntry = {jobId: string; index: number; name: string; value: string}
type PipelineStepSecretsState = Record<string, Record<string, Record<string, StepSecretEntry>>>
const pipelineStepSecretsInitialState: PipelineStepSecretsState = {}
export const pipelineStepSecrets = createSlice({
  name: 'pipelineStepSecrets',
  initialState: pipelineStepSecretsInitialState,
  reducers: {
    set: (
      state,
      action: PayloadAction<
        {pipelineId: PipelineId; branch: string; key: string} & StepSecretEntry
      >,
    ) => {
      const {pipelineId, branch, key, jobId, index, name, value} = action.payload
      const byBranch = (state[pipelineId] ??= {})
      const branchSecrets = (byBranch[branch] ??= {})
      branchSecrets[key] = {jobId, index, name, value}
    },
    unset: (
      state,
      action: PayloadAction<{pipelineId: PipelineId; branch: string; key: string}>,
    ) => {
      const {pipelineId, branch, key} = action.payload
      delete state[pipelineId]?.[branch]?.[key]
      deleteNestedIfEmpty(state, pipelineId, branch)
    },
    unsetAll: (state, action: PayloadAction<{pipelineId: PipelineId; branch: string}>) => {
      const {pipelineId, branch} = action.payload
      delete state[pipelineId]?.[branch]
      deleteIfEmpty(state, pipelineId)
    },
  },
})

export const debug = createSlice({
  name: 'debugging',
  initialState: debugInitialState,
  reducers: {
    start(state, action: PayloadAction<{pipelineId: PipelineId; branch: string; jobId: string}>) {
      const {pipelineId: id, branch, jobId} = action.payload
      const debugEntry = ensureDebugEntry(state, id, branch)
      debugEntry.isRunning = true
      debugEntry.isPanelOpen = true
      debugEntry.isPanelExpanded = true
      debugEntry.jobId = jobId
      debugEntry.runId = null
    },
    stop(state, action: PayloadAction<{pipelineId: PipelineId; branch: string}>) {
      const {pipelineId: id, branch} = action.payload
      const debugEntry = getDebugEntry(state, id, branch)
      if (debugEntry == null) {
        return
      }
      debugEntry.isRunning = false
      debugEntry.isPanelOpen = false
      debugEntry.runId = null
      debugEntry.jobId = null
      debugEntry.yaml = null
    },
    finish(state, action: PayloadAction<{pipelineId: PipelineId; branch: string}>) {
      const {pipelineId: id, branch} = action.payload
      const debugEntry = getDebugEntry(state, id, branch)
      if (debugEntry == null) {
        return
      }
      debugEntry.isRunning = false
    },
    expandPanel(
      state,
      action: PayloadAction<{pipelineId: PipelineId; branch: string; expanded: boolean}>,
    ) {
      const {pipelineId: id, branch, expanded} = action.payload
      const debugEntry = getDebugEntry(state, id, branch)
      if (debugEntry == null) {
        return
      }
      debugEntry.isPanelExpanded = expanded
    },
    toggleOpenPanel(
      state,
      action: PayloadAction<{pipelineId: PipelineId; branch: string; open: boolean}>,
    ) {
      const {pipelineId: id, branch, open} = action.payload
      const debugEntry = getDebugEntry(state, id, branch)
      if (debugEntry == null) {
        return
      }
      debugEntry.isPanelOpen = open
      debugEntry.isPanelExpanded = open
    },
  },
  extraReducers: builder => {
    builder.addMatcher(pipelinesApi.endpoints.runPipelineDebug.matchFulfilled, (state, action) => {
      const {originalArgs: args} = action.meta.arg
      const data = action.payload
      const id = args.pipelineId
      const branch = args.body.branch
      const yaml = args.body.yaml
      const runId = data.build.id
      const debugEntry = ensureDebugEntry(state, id, branch)
      debugEntry.runId = Number(runId)
      debugEntry.yaml = yaml
    })
  },
})

export const pipelineDependencyDetails = createSlice({
  name: 'pipelineDependencyDetails',
  initialState: pipelineDependencyDetailsInitialState,
  reducers: {
    add: (
      state,
      action: PayloadAction<{
        pipelineId: PipelineId
        branch: string
        dependencyId: PipelineDependencyId
        value: PipelineDependencyDetails
      }>,
    ) => {
      const {pipelineId, branch, dependencyId, value} = action.payload
      const detailsEntry = ensureDependencyDetailsEntry(state, pipelineId, branch)
      detailsEntry[dependencyId] = castDraft(value)
    },
  },
})

export const pipelineKotlinDsl = createSlice({
  name: 'pipelineKotlinDsl',
  initialState: pipelineKotlinDslInitialState,
  reducers: {
    setDslCode: (state, action: PayloadAction<{id: string; dslCode: string}>) => {
      const {id, dslCode} = action.payload

      if (state[id]?.dslCode === dslCode) {
        return
      }

      if (state[id]) {
        state[id].dslCode = dslCode
      } else {
        state[id] = {dslCode}
      }
    },
    setError: (state, action: PayloadAction<{id: string; error: string}>) => {
      const {id, error} = action.payload
      if (state[id]) {
        state[id].error = error
      } else {
        state[id] = {error}
      }
    },
    reset: (state, action: PayloadAction<string>) => {
      delete state[action.payload]
    },
  },
})
